/**
 * Meeting Modules Redux Slice
 * Manages meeting categories, modules, and selection state
 * Implements cache with 5-minute TTL
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MeetingModulesState } from "@/types/meeting-modules.types";
import { fetchMeetingModules } from "@/services/api/endpoints/meeting-modules.api";

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

const initialState: MeetingModulesState = {
  categories: [],
  loading: false,
  error: null,
  lastFetched: null,
  selectedCategoryId: null,
  selectedModuleId: null,
};

/**
 * Fetch meeting modules with automatic cache validation
 * Only fetches if cache is stale (>5 minutes old) or force refresh
 */
export const getMeetingModules = createAsyncThunk(
  "meetingModules/fetchModules",
  async (
    { forceRefresh = false }: { forceRefresh?: boolean } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { meetingModules: MeetingModulesState };
      const { lastFetched, categories } = state.meetingModules;

      // Check cache validity
      if (!forceRefresh && lastFetched && categories.length > 0) {
        const cacheAge = Date.now() - lastFetched;
        if (cacheAge < CACHE_TTL) {
          // Return cached data without API call
          return { data: categories, cached: true };
        }
      }

      // Fetch fresh data
      const data = await fetchMeetingModules();
      return { data, cached: false };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch meeting modules"
      );
    }
  }
);

const meetingModulesSlice = createSlice({
  name: "meetingModules",
  initialState,
  reducers: {
    // Select a category
    selectCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
      state.selectedModuleId = null; // Reset module selection
    },

    // Select a module
    selectModule: (state, action: PayloadAction<string | null>) => {
      state.selectedModuleId = action.payload;
    },

    // Clear selection
    clearSelection: (state) => {
      state.selectedCategoryId = null;
      state.selectedModuleId = null;
    },

    // Invalidate cache (useful for manual refresh)
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getMeetingModules - pending
      .addCase(getMeetingModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // getMeetingModules - fulfilled
      .addCase(getMeetingModules.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data;
        // Only update lastFetched if fresh data (not from cache)
        if (!action.payload.cached) {
          state.lastFetched = Date.now();
        }
      })
      // getMeetingModules - rejected
      .addCase(getMeetingModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { selectCategory, selectModule, clearSelection, invalidateCache } =
  meetingModulesSlice.actions;

export default meetingModulesSlice.reducer;
